import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MagnifyingGlassIcon, TruckIcon, ArrowRightIcon, UserIcon, ReceiptIcon } from "@phosphor-icons/react";

const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const iso = (d) => d.toISOString().slice(0, 10);
const LS_DEFAULT_CYL = "deliveries.defaultCylTypeId";

export default function Deliveries() {
  const [customers, setCustomers] = useState([]);
  const [cylTypes, setCylTypes] = useState([]);
  const [recent, setRecent] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);      // customer object
  const [showDrop, setShowDrop] = useState(false);
  const [defaultCylId, setDefaultCylId] = useState(
    () => localStorage.getItem(LS_DEFAULT_CYL) || ""
  );
  const inputRef = useRef(null);

  const loadAll = useCallback(async () => {
    try {
      const [c, t, r] = await Promise.all([
        api.get("/customers"),
        api.get("/cylinder-types"),
        api.get("/customer-deliveries?limit=100"),
      ]);
      setCustomers((c.data || []).filter((x) => x.category === "commercial"));
      setCylTypes(t.data || []);
      setRecent(r.data || []);
    } catch { toast.error("Failed to load"); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const suggestions = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return customers.slice(0, 8);
    return customers.filter((c) =>
      (c.name || "").toLowerCase().includes(term) ||
      (c.phone || "").includes(term)
    ).slice(0, 12);
  }, [q, customers]);

  const pick = (c) => {
    setSelected(c);
    setQ(c.name);
    setShowDrop(false);
  };

  return (
    <div className="space-y-8" data-testid="deliveries-page">
      <div className="border-b border-slate-200 pb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline text-brand-orange-ink">Commercial cylinders</div>
          <h1 className="font-display text-4xl font-black text-brand-blue">Deliveries</h1>
          <p className="text-slate-500 mt-2">Search a customer, log filled-given / empty-received and payment in one shot — or record a payment-only entry (0 cylinders) to settle old outstanding.</p>
        </div>
        <div className="w-full md:w-64" data-testid="page-default-cyl">
          <Label className="overline">Default cylinder for new entries</Label>
          <Select
            value={defaultCylId || "__none__"}
            onValueChange={(v) => {
              const next = v === "__none__" ? "" : v;
              setDefaultCylId(next);
              if (next) localStorage.setItem(LS_DEFAULT_CYL, next);
              else localStorage.removeItem(LS_DEFAULT_CYL);
              toast.success(next
                ? `Default set — ${cylTypes.find((t) => t.id === next)?.name || ""}`
                : "Default cleared");
            }}
          >
            <SelectTrigger className="rounded-none" data-testid="page-default-cyl-select">
              <SelectValue placeholder="No default" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No default</SelectItem>
              {cylTypes.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1">
            Pre-selected on every new delivery. A customer's saved default still wins.
          </p>
        </div>
      </div>

      {/* Search + quick form */}
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-8">
        {/* SEARCH */}
        <div className="space-y-3">
          <div className="overline text-brand-blue">Find customer</div>
          <div className="relative">
            <div className="flex items-center gap-2 border border-slate-300 focus-within:border-brand-blue bg-white h-11 px-3">
              <MagnifyingGlassIcon size={16} className="text-brand-blue" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => { setQ(e.target.value); setShowDrop(true); setSelected(null); }}
                onFocus={() => setShowDrop(true)}
                placeholder="Type name or phone…"
                className="flex-1 outline-none text-sm bg-transparent font-mono-num"
                data-testid="delivery-customer-search"
              />
            </div>
            {showDrop && suggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 max-h-80 overflow-y-auto border border-slate-300 bg-white shadow-lg z-20" data-testid="delivery-suggestions">
                {suggestions.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => pick(c)}
                    className="w-full text-left px-3 py-2 hover:bg-brand-orange-tint border-b border-slate-100 last:border-b-0"
                    data-testid={`delivery-suggest-${c.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <UserIcon size={12} className="text-brand-blue" />
                      <span className="font-semibold text-slate-900 text-sm">{c.name}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 font-mono-num pl-5">
                      {c.phone || "no phone"} · rate {money(c.rate)} · limit {money(c.credit_limit)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selected && (
            <div className="border border-brand-blue bg-brand-blue-tint p-4 space-y-1" data-testid="delivery-selected-card">
              <div className="overline text-brand-blue">Selected</div>
              <div className="font-display font-black text-brand-blue text-xl">{selected.name}</div>
              <div className="text-xs text-slate-600 font-mono-num">
                {selected.phone || "—"} · rate {money(selected.rate)} · disc {money(selected.discount)} · limit {money(selected.credit_limit)}
              </div>
              <Link
                to={`/customers/${selected.id}`}
                className="text-xs uppercase tracking-wider text-brand-orange-ink hover:text-brand-orange inline-flex items-center mt-2"
                data-testid="delivery-open-ledger"
              >
                Open full ledger <ArrowRightIcon size={12} className="ml-1" />
              </Link>
            </div>
          )}
        </div>

        {/* QUICK ENTRY */}
        <QuickEntryForm
          customer={selected}
          cylTypes={cylTypes}
          pageDefaultCylId={defaultCylId}
          onDone={() => { setSelected(null); setQ(""); loadAll(); }}
        />
      </div>

      {/* Recent */}
      <section className="pt-4 border-t border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline text-brand-orange-ink">Latest</div>
            <h2 className="font-display text-3xl font-black text-brand-blue">Recent deliveries</h2>
          </div>
          <div className="overline text-slate-500">{recent.length} entries</div>
        </div>
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-brand-blue-tint border-b border-slate-200">
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Date</TableHead>
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Customer</TableHead>
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Cylinder</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Filled</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Empty</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Amount</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Paid</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Credit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent.slice(0, 50).map((d, i) => (
                <TableRow key={d.id} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`recent-delivery-${i}`}>
                  <TableCell className="font-mono-num text-xs text-slate-500">{d.date}</TableCell>
                  <TableCell className="font-semibold">
                    <Link to={`/customers/${d.customer_id}`} className="hover:text-brand-orange-ink">{d.customer_name}</Link>
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm">{d.cylinder_name}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">{d.filled_given}</TableCell>
                  <TableCell className="text-right font-mono-num">{d.empty_received}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">{money(d.amount)}</TableCell>
                  <TableCell className="text-right font-mono-num">{money(d.payment_amount)}</TableCell>
                  <TableCell className="text-right font-mono-num">
                    <span className={d.credit > 0.01 ? "text-[#FF2400] font-semibold" : "text-slate-400"}>{money(d.credit)}</span>
                  </TableCell>
                </TableRow>
              ))}
              {recent.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10">No deliveries yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}

function QuickEntryForm({ customer, cylTypes, pageDefaultCylId, onDone }) {
  const today = iso(new Date());
  const [cylId, setCylId] = useState("");
  const [filled, setFilled] = useState("0");
  const [empty, setEmpty] = useState("0");
  const [rate, setRate] = useState("");
  const [disc, setDisc] = useState("");
  const [mode, setMode] = useState("cash");
  const [paid, setPaid] = useState("0");
  const [invNo, setInvNo] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(today);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (customer) {
      setRate(String(customer.rate ?? 0));
      setDisc(String(customer.discount ?? 0));
      // Priority: customer's saved default > page-level default > keep current
      if (customer.default_cylinder_type_id) {
        setCylId(customer.default_cylinder_type_id);
      } else if (pageDefaultCylId) {
        setCylId(pageDefaultCylId);
      }
    } else {
      setRate(""); setDisc("");
    }
  }, [customer, pageDefaultCylId]);

  useEffect(() => {
    // Page default wins over "first-type" fallback when nothing is selected
    if (cylTypes.length && !cylId) {
      setCylId(pageDefaultCylId && cylTypes.some((t) => t.id === pageDefaultCylId)
        ? pageDefaultCylId
        : cylTypes[0].id);
    }
  }, [cylTypes, cylId, pageDefaultCylId]);

  const netRate = Math.max(0, (parseFloat(rate) || 0) - (parseFloat(disc) || 0));
  const amount = netRate * (parseFloat(filled) || 0);
  const credit = Math.max(0, amount - (parseFloat(paid) || 0));
  const isPaymentOnly = (parseFloat(filled) || 0) <= 0 && (parseFloat(empty) || 0) <= 0 && (parseFloat(paid) || 0) > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!customer) return toast.error("Pick a customer first");
    if (!cylId) return toast.error("Pick a cylinder type");
    const fVal = parseFloat(filled) || 0;
    const eVal = parseFloat(empty) || 0;
    const pVal = parseFloat(paid) || 0;
    if (fVal <= 0 && eVal <= 0 && pVal <= 0) {
      return toast.error("Nothing to record — enter cylinders or a payment amount");
    }
    setSaving(true);
    try {
      await api.post("/customer-deliveries", {
        customer_id: customer.id,
        cylinder_type_id: cylId,
        filled_given: parseInt(filled) || 0,
        empty_received: parseInt(empty) || 0,
        rate: rate === "" ? null : parseFloat(rate),
        discount: disc === "" ? null : parseFloat(disc),
        payment_mode: pVal > 0 ? mode : "",
        payment_amount: pVal,
        invoice_no: invNo,
        note,
        date,
      });
      const isPaymentOnly = fVal <= 0 && eVal <= 0 && pVal > 0;
      toast.success(isPaymentOnly
        ? `Payment ${money(pVal)} recorded for ${customer.name}`
        : `Delivery logged for ${customer.name}`);
      setFilled("0"); setEmpty("0"); setPaid("0"); setInvNo(""); setNote("");
      setDate(today);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const disabled = !customer;

  let submitLabel;
  if (saving) submitLabel = "Saving…";
  else if (disabled) submitLabel = "Pick a customer to enable";
  else if (isPaymentOnly) submitLabel = `Record payment for ${customer.name}`;
  else submitLabel = `Log delivery for ${customer.name}`;

  return (
    <form onSubmit={submit} className={`border border-slate-300 bg-white p-6 space-y-4 ${disabled ? "opacity-60" : ""}`} data-testid="delivery-quick-form">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TruckIcon size={18} weight="fill" className="text-brand-orange" />
          <div className="overline text-brand-blue">Quick delivery entry</div>
        </div>
        {isPaymentOnly && (
          <div className="text-xs uppercase tracking-wider font-semibold text-brand-orange-ink border border-brand-orange px-2 py-0.5" data-testid="payment-only-badge">
            Payment-only
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="overline">Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none" data-testid="delivery-date" />
        </div>
        <div className="sm:col-span-2">
          <Label className="overline">Cylinder</Label>
          <Select value={cylId} onValueChange={setCylId} disabled={disabled}>
            <SelectTrigger className="rounded-none" data-testid="delivery-cylinder-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {cylTypes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Filled Given</Label>
          <Input type="number" min="0" value={filled} onChange={(e) => setFilled(e.target.value)} className="rounded-none font-mono-num" data-testid="delivery-filled" disabled={disabled} />
        </div>
        <div>
          <Label className="overline">Empty Received</Label>
          <Input type="number" min="0" value={empty} onChange={(e) => setEmpty(e.target.value)} className="rounded-none font-mono-num" data-testid="delivery-empty" disabled={disabled} />
        </div>
        <div>
          <Label className="overline">Rate (₹)</Label>
          <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="rounded-none font-mono-num" data-testid="delivery-rate" disabled={disabled} />
        </div>
        <div>
          <Label className="overline">Discount</Label>
          <Input type="number" step="0.01" value={disc} onChange={(e) => setDisc(e.target.value)} className="rounded-none font-mono-num" data-testid="delivery-discount" disabled={disabled} />
        </div>
        <div>
          <Label className="overline">Payment Mode</Label>
          <Select value={mode} onValueChange={setMode} disabled={disabled}>
            <SelectTrigger className="rounded-none" data-testid="delivery-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="gpay">GPay / UPI</SelectItem>
              <SelectItem value="wallet">IOC Paid</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline">Payment ₹</Label>
          <Input type="number" step="0.01" min="0" value={paid} onChange={(e) => setPaid(e.target.value)} className="rounded-none font-mono-num" data-testid="delivery-paid" disabled={disabled} />
        </div>
        <div className="sm:col-span-2">
          <Label className="overline">Invoice #</Label>
          <Input value={invNo} onChange={(e) => setInvNo(e.target.value)} placeholder="Optional" className="rounded-none font-mono-num" data-testid="delivery-invoice" disabled={disabled} />
        </div>
        <div className="sm:col-span-3">
          <Label className="overline">Note</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" data-testid="delivery-note" disabled={disabled} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-0 border border-slate-300 bg-brand-blue-tint" data-testid="delivery-calc">
        <div className="p-3 border-r border-slate-300">
          <div className="overline text-brand-blue text-xs">Net rate</div>
          <div className="font-mono-num font-bold text-brand-blue">{money(netRate)}</div>
        </div>
        <div className="p-3 border-r border-slate-300">
          <div className="overline text-brand-blue text-xs">Amount</div>
          <div className="font-mono-num font-bold text-brand-blue">{money(amount)}</div>
        </div>
        <div className="p-3">
          <div className="overline text-brand-orange-ink text-xs">Credit</div>
          <div className={`font-mono-num font-bold ${credit > 0.01 ? "text-[#FF2400]" : "text-slate-500"}`}>{money(credit)}</div>
        </div>
      </div>

      <Button
        type="submit"
        disabled={disabled || saving}
        className="w-full rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white h-11 disabled:opacity-50"
        data-testid="delivery-submit"
      >
        <ReceiptIcon size={16} className="mr-2" />
        {submitLabel}
      </Button>
    </form>
  );
}
